import axios from 'axios';
import { useAuthStore } from '../stores/authStore';
import type {
  PredictionRequest,
  PredictionResponse,
  BatchPredictionResponse,
  InverterList,
  InverterDetail,
  ComponentDetail,
  SiteDetail,
  TrainingRequest,
  TrainingMetrics,
  HealthCheck,
  Alert,
  MaintenancePlan,
  DataSource,
  WeatherData,
  DriftStatus,
  IndustrialPreset,
  ModelComparisonResponse,
} from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// JWT + active-project + tenant interceptor
api.interceptors.request.use((config) => {
  const { token, user } = useAuthStore.getState();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (user?.tenant_id) {
    config.headers['X-Tenant-Id'] = user.tenant_id;
  }
  // Read activeProjectId straight from persisted localStorage to avoid a circular
  // import with projectStore (which imports api.ts for projectsApi).
  try {
    const raw = localStorage.getItem('inverterai-project');
    if (raw) {
      const parsed = JSON.parse(raw) as { state?: { activeProjectId?: number | null } };
      const pid = parsed?.state?.activeProjectId;
      if (typeof pid === 'number' && pid > 0) {
        config.headers['X-Project-Id'] = String(pid);
      }
    }
  } catch {
    // ignore — header simply not set
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().logout();
    }
    return Promise.reject(error);
  }
);

// ============================================================================
// HEALTH
// ============================================================================

export const healthApi = {
  check: async (): Promise<HealthCheck> => {
    const { data } = await api.get('/health');
    return data;
  },
};

// ============================================================================
// PREDICTIONS
// ============================================================================

export const predictionApi = {
  create: async (request: PredictionRequest): Promise<PredictionResponse> => {
    const { data } = await api.post('/predictions', request);
    return data;
  },
  compare: async (request: PredictionRequest): Promise<ModelComparisonResponse> => {
    const { data } = await api.post('/predictions/compare', request);
    return data;
  },
  batch: async (requests: PredictionRequest[]): Promise<BatchPredictionResponse> => {
    const { data } = await api.post('/predictions/batch', { predictions: requests });
    return data;
  },
  history: async (params?: { limit?: number; offset?: number }): Promise<PredictionResponse[]> => {
    const { data } = await api.get('/history', { params });
    return data;
  },
};

// ============================================================================
// INVERTERS
// ============================================================================

export const inverterApi = {
  list: async (params?: { type?: string; status?: string }): Promise<InverterList> => {
    const { data } = await api.get('/inverters', { params });
    return data;
  },
  get: async (id: number): Promise<InverterDetail> => {
    const { data } = await api.get(`/inverters/${id}`);
    return data;
  },
  create: async (inverter: Partial<InverterDetail>): Promise<InverterDetail> => {
    const { data } = await api.post('/inverters', inverter);
    return data;
  },
};

// ============================================================================
// COMPONENTS
// ============================================================================

export const componentApi = {
  list: async (inverterId?: number): Promise<ComponentDetail[]> => {
    const { data } = await api.get('/components', { params: { inverter_id: inverterId } });
    return data;
  },
  get: async (id: number): Promise<ComponentDetail> => {
    const { data } = await api.get(`/components/${id}`);
    return data;
  },
};

// ============================================================================
// SITES
// ============================================================================

export const siteApi = {
  list: async (): Promise<SiteDetail[]> => {
    const { data } = await api.get('/sites');
    return data;
  },
  get: async (id: number): Promise<SiteDetail> => {
    const { data } = await api.get(`/sites/${id}`);
    return data;
  },
  create: async (site: Partial<SiteDetail>): Promise<SiteDetail> => {
    const { data } = await api.post('/sites', site);
    return data;
  },
};

// ============================================================================
// TRAINING
// ============================================================================

export const trainingApi = {
  start: async (request: TrainingRequest): Promise<TrainingMetrics> => {
    const { data } = await api.post('/training/start', request);
    return data;
  },
  metrics: async (params?: string | { data_source?: string; model_type?: string }): Promise<TrainingMetrics> => {
    if (typeof params === 'string') {
      // Legacy: session ID lookup
      const { data } = await api.get(`/training/metrics/${params}`);
      return data;
    }
    const { data } = await api.get('/training/metrics', { params });
    return data;
  },
  history: async (): Promise<TrainingMetrics[]> => {
    const { data } = await api.get('/training/history');
    return data;
  },
  exportOnnx: async (): Promise<Blob> => {
    const { data } = await api.get('/training/export/onnx', { responseType: 'blob' });
    return data;
  },
  qcSummary: async (projectId: number = 1) => {
    const { data } = await api.get(`/training/qc-summary?project_id=${projectId}`);
    return data;
  },
  featureCorrelations: async (projectId: number = 1) => {
    const { data } = await api.get(`/training/feature-correlations?project_id=${projectId}`);
    return data;
  },
};

// ============================================================================
// ALERTS
// ============================================================================

export interface IgbtScoreComponent {
  name: string;
  label: string;
  raw_score: number;
  weight: number;
  contribution: number;
  detail: string;
  physics: string;
  value?: number;
  unit?: string;
  precision?: number;
  value_label?: string;
}

export interface IgbtPrefailureItem {
  inverter_id: number;
  serial_number: string;
  block: string;
  model: string | null;
  manufacturer: string | null;
  rated_power_kw: number | null;
  score: number;
  ttf_hours: number;
  rul_months?: number;
  prob_fail_24h: number;
  prob_fail_7d: number;
  prob_fail_30d: number;
  prob_fail_90d: number;
  severity: 'critical' | 'warning' | 'info';
  features: Record<string, number>;
  score_components: IgbtScoreComponent[];
  top_drivers: Array<{ name: string; label?: string; weight: number; detail: string }>;
  last_timestamp: string;
  model_source?: 'pinn' | 'heuristic' | 'heuristic_fallback';
  weights_source?: 'pinn_ig' | 'admin_override' | 'heuristic' | 'heuristic_fallback';
}

export interface IgbtPrefailureResponse {
  project_id: number | null;
  window_h: number;
  reference_timestamp?: string;
  generated_at: string;
  items: IgbtPrefailureItem[];
  summary: { total: number; critical: number; warning: number; info: number };
  method?: string;
  model_source?: 'pinn' | 'heuristic';
  weights_source?: 'pinn_ig' | 'admin_override' | 'heuristic' | 'heuristic_fallback';
}

export const alertApi = {
  list: async (params?: { status?: string; severity?: string }): Promise<Alert[]> => {
    const { data } = await api.get('/alerts', { params });
    return data;
  },
  acknowledge: async (id: number): Promise<Alert> => {
    const { data } = await api.patch(`/alerts/${id}/acknowledge`);
    return data;
  },
  resolve: async (id: number): Promise<Alert> => {
    const { data } = await api.patch(`/alerts/${id}/resolve`);
    return data;
  },
  igbtPrefailure: async (params?: { window_h?: number; limit?: number; only_critical?: boolean }): Promise<IgbtPrefailureResponse> => {
    const { data } = await api.get('/alerts/igbt-prefailure', { params });
    return data;
  },
};

// ============================================================================
// PINN
// ============================================================================

export interface PhysicsLawEvaluation {
  name: string;
  category: string;
  formula: string;
  loss: number;
  status: 'pass' | 'warning' | 'violation';
  description: string;
}

export interface PINNPerInverterResponse {
  inverter_id: number;
  serial_number: string;
  model: string | null;
  manufacturer: string | null;
  rated_power_kw: number | null;
  window_h: number;
  last_timestamp: string;
  n_samples: number;
  rul_months: number;
  rul_years: number;
  ci95_lower: number | null;
  ci95_upper: number | null;
  uncertainty_std: number | null;
  features: Record<string, number>;
  derived: Record<string, number>;
  component_rul: Record<string, number>;
  physics_laws: PhysicsLawEvaluation[];
  constraints_pass: number;
  constraints_warning: number;
  constraints_violation: number;
  constraints_total: number;
  model_status: string;
  timestamp: string;
}

export interface FeatureWeightEntry {
  name: string;
  weight: number;
  source: string;
  available: boolean;
  description?: string;
  would_detect?: string;
  requires_hardware?: boolean;
}

export interface FeatureWeightsResponse {
  scada_features: FeatureWeightEntry[];
  hardware_features: FeatureWeightEntry[];
  weights_source: 'pinn_ig' | 'admin_override' | 'heuristic_fallback';
  weights_sum: number;
  method?: string;
  n_samples?: number;
  n_steps?: number;
  training_date?: string;
  override_applied: boolean;
  override_updated_at?: string;
  override_updated_by?: string;
  override_note?: string;
}

export interface PINNStatus {
  is_trained: boolean;
  has_torch: boolean;
  model_path: string | null;
  val_r2: number | null;
  val_rmse: number | null;
  val_mae: number | null;
  constraints_pass: number | null;
  constraints_warning: number | null;
  constraints_violation: number | null;
  constraints_total: number | null;
  training_date?: string;
  epochs_trained?: number;
  n_train?: number;
  n_val?: number;
}

export const pinnApi = {
  status: async (): Promise<PINNStatus> => {
    const { data } = await api.get('/pinn/status');
    return data;
  },
  perInverter: async (params: {
    inverter_id?: number;
    serial_number?: string;
    window_h?: number;
  }): Promise<PINNPerInverterResponse> => {
    const { data } = await api.post('/pinn/per-inverter', params);
    return data;
  },
  featureWeights: async (): Promise<FeatureWeightsResponse> => {
    const { data } = await api.get('/pinn/feature-weights');
    return data;
  },
  updateFeatureWeights: async (payload: {
    weights?: Record<string, number>;
    reset?: boolean;
    note?: string;
  }): Promise<FeatureWeightsResponse> => {
    const { data } = await api.put('/pinn/feature-weights', payload);
    return data;
  },
};

// ============================================================================
// TEMPORAL PREDICTION
// ============================================================================

export const temporalApi = {
  predict: async (request: { inverter_id: number; horizon_years: number }): Promise<unknown> => {
    const { data } = await api.post('/temporal', request);
    return data;
  },
};

// ============================================================================
// WEATHER
// ============================================================================

export const weatherApi = {
  get: async (siteId: number): Promise<WeatherData[]> => {
    const { data } = await api.get(`/weather/${siteId}`);
    return data;
  },
  import: async (request: { site_id: number; latitude: number; longitude: number; year: number; source: string }): Promise<unknown> => {
    const { data } = await api.post('/weather/import', request);
    return data;
  },
};

// ============================================================================
// THERMAL
// ============================================================================

export const thermalApi = {
  profile: async (inverterId: number): Promise<unknown> => {
    const { data } = await api.get(`/thermal/profile/${inverterId}`);
    return data;
  },
  cycles: async (componentId: number): Promise<unknown> => {
    const { data } = await api.get(`/thermal/cycles/${componentId}`);
    return data;
  },
};

// ============================================================================
// DRIFT
// ============================================================================

export const driftApi = {
  status: async (): Promise<DriftStatus> => {
    const { data } = await api.get('/drift/status');
    return data;
  },
  history: async (daysBack: number = 30): Promise<unknown[]> => {
    const { data } = await api.get('/drift/history', { params: { days_back: daysBack } });
    return data;
  },
};

// ============================================================================
// VALIDATION
// ============================================================================

export const validationApi = {
  physics: async (): Promise<unknown> => {
    const { data } = await api.post('/validation/physics');
    return data;
  },
  scores: async (): Promise<unknown> => {
    const { data } = await api.get('/validation/physics-scores');
    return data;
  },
};

// ============================================================================
// REPORTS
// ============================================================================

export const reportApi = {
  generate: async (params: { type: string; format: string }): Promise<unknown> => {
    const { data } = await api.post('/reports/generate', params);
    return data;
  },
};

// ============================================================================
// MAINTENANCE
// ============================================================================

export const maintenanceApi = {
  list: async (): Promise<MaintenancePlan[]> => {
    const { data } = await api.get('/maintenance');
    return data;
  },
  create: async (plan: Partial<MaintenancePlan>): Promise<MaintenancePlan> => {
    const { data } = await api.post('/maintenance/plan', plan);
    return data;
  },
};

// ============================================================================
// PRESETS
// ============================================================================

export const presetsApi = {
  list: async (params?: { inverter_type?: string }): Promise<IndustrialPreset[]> => {
    const { data } = await api.get('/presets', { params });
    return data;
  },
  get: async (id: string): Promise<IndustrialPreset> => {
    const { data } = await api.get(`/presets/${id}`);
    return data;
  },
};

// ============================================================================
// DATA SOURCES
// ============================================================================

export const dataSourceApi = {
  list: async (params?: { source_type?: string }): Promise<DataSource[]> => {
    const { data } = await api.get('/datasets', { params });
    return data;
  },
  upload: async (file: File, meta: Record<string, string>): Promise<unknown> => {
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(meta).forEach(([k, v]) => formData.append(k, v));
    const { data } = await api.post('/datasets/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
  importScada: async (
    file: File,
    options: {
      columnMapping?: Record<string, string>;
      timezone?: string;
      powerUnit?: string;
      temperatureUnit?: string;
      insertToDb?: boolean;
    } = {},
  ): Promise<unknown> => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.columnMapping) {
      formData.append('column_mapping_json', JSON.stringify(options.columnMapping));
    }
    formData.append('timezone', options.timezone ?? 'UTC');
    formData.append('power_unit', options.powerUnit ?? 'kw');
    formData.append('temperature_unit', options.temperatureUnit ?? 'c');
    formData.append('insert_to_db', String(options.insertToDb ?? true));
    const { data } = await api.post('/datasets/import-scada', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
};

// ============================================================================
// DIGITAL TWIN
// ============================================================================

export interface DigitalTwinSimulateRequest {
  temperature_ambient_c: number;
  irradiance_wm2: number;
  load_percent: number;
  dc_voltage_v: number;
  operating_hours: number;
}

export interface DigitalTwinSimulateResult {
  // Power
  dc_power_kw: number;
  ac_power_kw: number;
  power_loss_kw: number;
  efficiency_percent: number;
  // Thermal
  tj_estimated_c: number;
  t_heatsink_c: number;
  thermal_headroom_k: number;
  p_conduction_w: number;
  p_switching_w: number;
  // Electrical
  ac_current_a: number;
  // Degradation
  igbt_rul_months: number;
  capacitor_rul_months: number;
  limiting_component: string;
  // Validation
  kirchhoff_consistent: boolean;
  physics_warnings: string[];
  // Metadata
  model_version: string;
  simulation_timestamp: string;
}

export interface AnomalyDetectRequest {
  simulated: DigitalTwinSimulateRequest;
  actual_scada: {
    dc_power_kw: number;
    ac_power_kw: number;
    temperature_heatsink_c: number;
    efficiency_percent: number;
    thd_current_percent: number;
  };
}

export interface AnomalyDeviation {
  parameter: string;
  label: string;
  simulated: number;
  actual: number;
  deviation_percent: number;
  anomaly: boolean;
  unit: string;
}

export interface AnomalyDetectResult {
  anomaly_detected: boolean;
  anomaly_score: number;
  deviations: AnomalyDeviation[];
  summary: string;
}

// ============================================================================
// MONITORING (Fleet Status)
// ============================================================================

export interface FleetInverter {
  id: number;
  serial_number: string;
  model: string;
  manufacturer: string;
  inverter_type: string;
  rated_power_kw: number;
  status: string;
  down?: boolean;  // live: never-generating in the sim quarter (F-005)
  site_id?: number | null;
  site_name?: string | null;
  site_climate_zone?: string | null;
  power_kw: number;
  efficiency: number;
  thd: number;
  t_heatsink: number;
  t_ambient: number;
  irradiance_wm2?: number;
  last_timestamp: string | null;
}

export interface ShiftEnergyResponse {
  shift: string;
  window_start?: string;
  window_end?: string;
  reference_timestamp?: string;
  energy_mwh: number;
  n_inverters: number;
  n_with_data: number;
  items: Array<{
    inverter_id: number;
    avg_power_kw: number;
    elapsed_hours: number;
    energy_kwh: number;
    n_samples: number;
  }>;
}

export const monitoringApi = {
  fleetStatus: async (): Promise<{ inverters: FleetInverter[]; total: number }> => {
    const { data } = await api.get('/inverters/fleet-status');
    return data;
  },
  shiftEnergy: async (shift: 'auto' | 'morning' | 'afternoon' | 'night' = 'auto'): Promise<ShiftEnergyResponse> => {
    const { data } = await api.get('/inverters/shift-energy', { params: { shift } });
    return data;
  },
  // Live-stream (sim-aware) time-series
  fleetTimeseries: async (window_h = 24): Promise<{ ref_now: string; n_points: number; points: { t: string; fleet_power_kw: number }[] }> => {
    const { data } = await api.get('/inverters/fleet-timeseries', { params: { window_h } });
    return data;
  },
  inverterTimeseries: async (inverter_id: number, window_h = 24): Promise<{ inverter_id: number; ref_now: string; points: { t: string; power_kw: number; t_heatsink: number; efficiency: number; thd: number }[] }> => {
    const { data } = await api.get('/inverters/time-series', { params: { inverter_id, window_h } });
    return data;
  },
};

// ============================================================================
// DIGITAL TWIN
// ============================================================================

export const digitalTwinApi = {
  status: async (inverterId: number): Promise<unknown> => {
    const { data } = await api.get(`/digital-twin/${inverterId}/status`);
    return data;
  },
  simulate: async (request: DigitalTwinSimulateRequest): Promise<DigitalTwinSimulateResult> => {
    const { data } = await api.post('/digital-twin/simulate', request);
    return data;
  },
  simulateFull: async (request: DigitalTwinSimulateRequest): Promise<DigitalTwinSimulateResult> => {
    const { data } = await api.post('/digital-twin/simulate-full', request);
    return data;
  },
  detectAnomaly: async (request: AnomalyDetectRequest): Promise<AnomalyDetectResult> => {
    const { data } = await api.post('/digital-twin/anomaly-detect', request);
    return data;
  },
};

// ============================================================================
// ASSISTANT
// ============================================================================

export interface AssistantChatRequest {
  message: string;
  language: string;
  role: string;
  context?: Record<string, unknown>;
  history?: { role: string; content: string }[];
}

export interface AssistantChatResponse {
  reply: string;
  intent: string;
  confidence: number;
  data?: Record<string, unknown>;
  actions?: string[];
  sources?: string[];
  timestamp: string;
}

export const assistantApi = {
  chat: async (request: AssistantChatRequest): Promise<AssistantChatResponse> => {
    const { data } = await api.post('/assistant/chat', request);
    return data;
  },
};

// ============================================================================
// PROJECTS & DATA INGESTION
// ============================================================================

export interface Project {
  id: number;
  name: string;
  client?: string;
  description?: string;
  plant_structure?: PlantStructureData;
  inverter_oem?: string;
  inverter_model?: string;
  rated_power_kw?: number;
  preset_id?: string;
  active: boolean;
  created_at?: string;
  updated_at?: string;
  site_count: number;
  inverter_count: number;
  import_count: number;
  total_scada_records: number;
}

export interface ProjectCreate {
  name: string;
  client?: string;
  description?: string;
  inverter_oem?: string;
  inverter_model?: string;
  rated_power_kw?: number;
  preset_id?: string;
}

export interface PlantBlock {
  id: string;
  name: string;
  inverter_count: number;
  inverters: string[];
}

export interface PlantStructureData {
  total_inverters: number;
  total_blocks: number;
  blocks: PlantBlock[];
  unmatched_assets?: string[];
}

export interface PlantStructure {
  project_id: number;
  project_name: string;
  total_inverters: number;
  total_blocks: number;
  blocks: PlantBlock[];
}

export interface DatasetImportRecord {
  id: number;
  project_id: number;
  filename?: string;
  format?: string;
  quarter?: string;
  records_total?: number;
  records_mapped?: number;
  records_inserted?: number;
  inverters_found?: number;
  signals_available?: number;
  signals_missing?: number;
  quality_score?: number;
  quality_report?: Record<string, unknown>;
  gap_analysis?: GapAnalysisItem[];
  status: string;
  created_at?: string;
  completed_at?: string;
}

export interface GapAnalysisItem {
  signal: string;
  priority: string;
  status: string;
  note: string;
}

export interface ImportResult {
  status: string;
  import_id: number;
  project_id: number;
  filename: string;
  records_inserted: number;
  inverters_created: number;
  quality_score: number;
  can_train: boolean;
  gap_analysis: GapAnalysisItem[];
}

export interface BlockStatistics {
  block_id: string;
  inverter_count: number;
  inverter_ids: string[];
  avg_power_kw?: number;
  max_power_kw?: number;
  avg_availability_pct?: number;
  record_count?: number;
  first_data?: string;
  last_data?: string;
}

export interface ProjectStatistics {
  project_id: number;
  project_name: string;
  total_inverters: number;
  total_blocks: number;
  window?: 'live' | 'all';
  window_from?: string | null;
  window_to?: string | null;
  blocks: BlockStatistics[];
}

export const projectsApi = {
  list: async (): Promise<Project[]> => {
    const { data } = await api.get('/projects');
    return data;
  },
  create: async (project: ProjectCreate): Promise<Project> => {
    const { data } = await api.post('/projects', project);
    return data;
  },
  get: async (id: number): Promise<Project> => {
    const { data } = await api.get(`/projects/${id}`);
    return data;
  },
  update: async (id: number, updates: Partial<ProjectCreate>): Promise<Project> => {
    const { data } = await api.put(`/projects/${id}`, updates);
    return data;
  },
  structure: async (id: number): Promise<PlantStructure> => {
    const { data } = await api.get(`/projects/${id}/structure`);
    return data;
  },
  imports: async (id: number): Promise<DatasetImportRecord[]> => {
    const { data } = await api.get(`/projects/${id}/imports`);
    return data;
  },
  statistics: async (id: number, window: 'live' | 'all' = 'live'): Promise<ProjectStatistics> => {
    const { data } = await api.get(`/projects/${id}/statistics`, { params: { window } });
    return data;
  },
  importParquet: async (projectId: number, formData: FormData): Promise<ImportResult> => {
    const { data } = await api.post(`/projects/${projectId}/import-parquet`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000, // 10 min for large Parquet files
    });
    return data;
  },
  qualitySummary: async (id: number): Promise<QualitySummary> => {
    const { data } = await api.get(`/projects/${id}/quality/summary`);
    return data;
  },
  qualityFlags: async (
    id: number,
    params: { action?: string; rule_code?: string; inverter_id?: number; page?: number; page_size?: number } = {},
  ): Promise<QualityFlagsPage> => {
    const { data } = await api.get(`/projects/${id}/quality/flags`, { params });
    return data;
  },
  qualityFlagsCsvUrl: (
    id: number,
    params: { action?: string; rule_code?: string; inverter_id?: number } = {},
  ): string => {
    const baseURL = api.defaults.baseURL || '';
    const qs = new URLSearchParams({ fmt: 'csv', ...Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '').map(([k, v]) => [k, String(v)])) });
    return `${baseURL}/projects/${id}/quality/flags?${qs.toString()}`;
  },
  qualityLoadedStats: async (id: number): Promise<QualityLoadedStats> => {
    const { data } = await api.get(`/projects/${id}/quality/loaded-stats`);
    return data;
  },
  qualityFleetMatrix: async (
    id: number,
    sort_by: 'serial' | 'excluded_pct' | 'review_pct' | 'warn_pct' | 'score' | 'total' = 'excluded_pct',
  ): Promise<QualityFleetMatrix> => {
    const { data } = await api.get(`/projects/${id}/quality/fleet-matrix`, { params: { sort_by } });
    return data;
  },
  qualityCanonical: async (id: number): Promise<QualityCanonical | null> => {
    try {
      const { data } = await api.get(`/projects/${id}/quality/canonical`);
      return data;
    } catch {
      return null;
    }
  },
};

// QC v2 canonical artefact (tmeic_rambler-specific). Surfaces the 3.2 M-row
// audit done 2026-05-17 with TMEIC-confirmed rescale factors and bounds.
export interface QualityCanonicalTopTag {
  tag: string;
  kind: string;
  unit?: string | null;
  oob_pct: number;
  oob_pct_before?: number;
  oob_pct_after?: number;
  nan_pct: number;
  n_observations: number;
  rescale_factor: number;
  bounds_effective?: [number, number] | null;
  validation_status?: string | null;
  tmeic_note?: string | null;
  min?: number | null;
  max?: number | null;
  p50?: number | null;
  p99?: number | null;
}
export interface QualityCanonicalRescaleProvenance {
  category: string;
  source: string;
  rationale: string;
  follow_up?: string;
}
export interface QualityCanonicalBoundRevision {
  date: string;
  tag: string;
  kind: string;
  old: [number, number];
  new: [number, number];
  reason: string;
  source: string;
}
export interface QualityCanonical {
  tenant_id: string;
  project_id: number;
  project_name: string;
  generated_at_utc: string;
  tmeic_email_reference: string;
  n_rows_processed: number;
  n_csvs_processed: number;
  n_batches_merged: number;
  fleet_kpi: {
    total_tag_observations: number;
    total_out_of_bounds: number;
    weighted_oob_pct: number;
    weighted_oob_pct_before?: number;
    weighted_oob_pct_after?: number;
    n_tags: number;
  };
  rescale_factors_applied: Record<string, number>;
  rescale_provenance: Record<string, QualityCanonicalRescaleProvenance>;
  bound_revision_log: QualityCanonicalBoundRevision[];
  tmeic_excluded_tags: Record<string, string>;
  a4_tightened_bounds: Record<string, [number, number]>;
  top_exclusion_by_tag: QualityCanonicalTopTag[];
}

// Quality types
export interface QualityRule {
  rule_code: string;
  action: 'exclude' | 'review' | 'keep_warn';
  count: number;
  pct: number;
  field?: string;
  threshold?: string;
  reason_es?: string;
  reason_en?: string;
}
export interface QualityOffender {
  inverter_id: number;
  serial_number: string;
  excluded: number;
  total: number;
  pct: number;
}
export interface QualityQuarter {
  quarter: string;
  rows: number;
  first?: string;
  last?: string;
}
export interface QualitySummary {
  project_id: number;
  project_name: string;
  client?: string;
  total_inverters: number;
  total_rows: number;
  flagged_total: number;
  kpi: { usable_rows: number; review_rows: number; excluded_rows: number; warn_rows: number; utilisation_rate: number };
  rules: QualityRule[];
  top_offenders: QualityOffender[];
  quarters: QualityQuarter[];
  generated_at: string;
}
export interface QualityFlag {
  flag_id: number;
  scada_id: number;
  inverter_id: number;
  serial_number: string;
  timestamp?: string;
  rule_code: string;
  action: string;
  field_name: string;
  original_value?: number;
  threshold?: string;
  reason: string;
  flagged_at?: string;
}
export interface QualityFlagsPage {
  project_id: number;
  filters: { action?: string; rule_code?: string; inverter_id?: number };
  page: number;
  page_size: number;
  total: number;
  items: QualityFlag[];
}
export interface QualitySignalStat {
  signal: string;
  unit: string;
  present: boolean;
  non_null_count?: number;
  null_pct?: number;
  mean?: number;
  min?: number;
  max?: number;
}
export interface QualityLoadedStats {
  project_id: number;
  total_usable_rows: number;
  signals: QualitySignalStat[];
  generated_at: string;
}
export interface QualityFleetRuleMeta {
  rule_code: string;
  action: 'exclude' | 'review' | 'keep_warn' | 'unknown';
  field?: string;
  threshold?: string;
  reason_es?: string;
  reason_en?: string;
}
export interface QualityFleetInverter {
  inverter_id: number;
  serial_number: string;
  site_id: number;
  site_name: string;
  total: number;
  usable: number;
  excluded: number;
  review: number;
  warn: number;
  excluded_pct: number;
  review_pct: number;
  warn_pct: number;
  score: number;
  rule_counts: Record<string, number>;
}
export interface QualityFleetSite {
  site_id: number;
  site_name: string;
  inverter_count: number;
  total: number;
  usable: number;
  excluded: number;
  review: number;
  warn: number;
  excluded_pct: number;
  review_pct: number;
  warn_pct: number;
}
export interface QualityFleetMatrix {
  project_id: number;
  project_name: string;
  total_inverters: number;
  rules: QualityFleetRuleMeta[];
  inverters: QualityFleetInverter[];
  sites: QualityFleetSite[];
  sort_by: string;
  generated_at: string;
}

// ============================================================================
// Fleet analytics (tmeic_rambler) — 4-pillar Health Score, Peck risk, Tier-2
// ============================================================================
export interface FleetHealthItem {
  inverter_id: string;
  // pillars are null for DOWN/non-generating units (appended without a computed score)
  health_score: number | null;
  fan_urgency: number | null;
  thermal_residual: number | null;
  dc_asymmetry: number | null;
  eol_covariate: number | null;
  fan_horizon_mo?: number | null;
  t_resid_c?: number;
  rank: number;
  traffic_light: 'GREEN' | 'YELLOW' | 'RED' | 'DOWN' | string;
}
export interface FleetHealthResponse {
  weights: Record<string, number>;
  n_inverters: number;
  n_red: number;
  n_yellow: number;
  dc_pillar?: string;
  anchor?: Record<string, any> | null;
  ranking: FleetHealthItem[];
  label: string;
  data_snapshot: string;
  method: string;
}

export const rcaApi = {
  caseStudyUf112: async (): Promise<any> => {
    const { data } = await api.get('/rca/case-study/uf112');
    return data;
  },
  analyze: async (inputs: any): Promise<any> => {
    // Uses the shared tenant-aware axios instance (auth header stamped) — NOT a raw fetch.
    const { data } = await api.post('/rca/analyze', inputs);
    return data;
  },
};

export interface SimState {
  tenant: string; dataset_split: string; sim_now: string; speed: number;
  paused: boolean; anchor_wall: string; q2_start: string; q2_end: string; progress_pct: number;
  display_now?: string; display_offset_ms?: number; display_anchor?: string;
  // SimLock + plant-local (Central Time) presentation
  locked?: boolean; plant_tz?: string; sim_now_local?: string;
}
export const simApi = {
  state: async (): Promise<SimState> => {
    const { data } = await api.get('/sim/state');
    return data;
  },
  control: async (body: { action: 'play' | 'pause' | 'seek' | 'set_speed'; seek_to?: string; speed?: number }): Promise<SimState> => {
    const { data } = await api.post('/sim/control', body);
    return data;
  },
};

// Per-inverter 4-pillar health (as_of time-travel) + pillar time-series (the Detail drill-down).
export interface PillarValue {
  value: number; window: string; time_resolved: boolean; note?: string; operating_hours_est?: number;
}
export interface InverterHealthAsOf {
  inverter_id: string; as_of_utc: string; mode: 'live' | 'as_of'; partition: 'train' | 'holdout';
  data_split_queried: string[]; health_score: number; traffic_light: string; risk_driver?: string;
  pillars: {
    fan_urgency: PillarValue; thermal_residual: PillarValue;
    dc_asymmetry: PillarValue; eol_covariate: PillarValue;
  };
  weights: Record<string, number>; ci_note: string; honest_value: string;
}
export interface InsulationResidual {
  signal: string; label: string; role: string; n_breach: number; breached: boolean; active: boolean;
  first_breach: string | null; days_since_breach: number | null; pre_event_breach: boolean;
  mu: number; sigma: number;
}
export interface InsulationDetail {
  inverter_id: string; available: boolean; residuals: InsulationResidual[];
}
export interface Last24hPoint { t: string; power_kw: number | null; temp_c: number | null; }
export interface Last24hProfile {
  inverter_id: string; as_of_utc: string; as_of_local: string; cadence_min: number;
  n: number; temp_kind: string; points: Last24hPoint[];
}
export interface PillarSeriesPoint {
  t: string; fan: number; thermal: number; dc: number; eol: number; health: number; partition: string;
}
export interface PillarSeries {
  inverter_id: string; start: string; end: string; step_h: number; n_points: number;
  weights: Record<string, number>; series: PillarSeriesPoint[]; note: string;
}

export const fleetApi = {
  healthScore: async (params?: { limit?: number }): Promise<FleetHealthResponse> => {
    const { data } = await api.get('/fleet/health-score', { params });
    return data;
  },
  peckRisk: async (): Promise<any> => {
    const { data } = await api.get('/fleet/peck-risk');
    return data;
  },
  tier2Alerts: async (): Promise<any> => {
    const { data } = await api.get('/fleet/tier2-alerts');
    return data;
  },
  // Per-inverter health AS OF an instant (omit asOf for the live/frozen value).
  inverterHealth: async (serial: string, asOf?: string): Promise<InverterHealthAsOf> => {
    const { data } = await api.get(`/fleet/inverter/${encodeURIComponent(serial)}/health`,
      { params: asOf ? { as_of: asOf } : {} });
    return data;
  },
  // Per-inverter Tier-2 insulation residuals (R_vpe ground-fault precursor + R_eff/R_vdc).
  inverterInsulation: async (serial: string): Promise<InsulationDetail> => {
    const { data } = await api.get(`/fleet/insulation/${encodeURIComponent(serial)}`);
    return data;
  },
  // Per-inverter REAL last-24h SCADA profile (AC power + measured cabinet temp) ending at sim_now.
  inverterLast24h: async (serial: string): Promise<Last24hProfile> => {
    const { data } = await api.get(`/fleet/inverter/${encodeURIComponent(serial)}/last24h`);
    return data;
  },
  // Per-inverter 4-pillar time-series over [start,end] (ISO-UTC) for the evolution chart.
  inverterPillarSeries: async (serial: string, start: string, end: string, stepH = 24): Promise<PillarSeries> => {
    const { data } = await api.get(`/fleet/inverter/${encodeURIComponent(serial)}/pillar-series`,
      { params: { start, end, step_h: stepH } });
    return data;
  },
  // Tier-2 short/medium-term early-warning (precursors + dc-onset + Peck). Omit asOf for live.
  earlyWarning: async (asOf?: string): Promise<EarlyWarningResponse> => {
    const { data } = await api.get('/fleet/early-warning', { params: asOf ? { as_of: asOf } : {} });
    return data;
  },
  // Prediction timeline: scope (plant/station/inverter) + resolution + range + mode (views/pillars).
  predictionTimeline: async (params?: {
    scope?: 'plant' | 'station' | 'inverter'; id?: string;
    resolution?: 'D' | 'W'; weeks_back?: number; mode?: 'views' | 'pillars';
    from?: string; to?: string;
  }): Promise<PredictionTimeline> => {
    const { data } = await api.get('/fleet/prediction-timeline', { params });
    return data;
  },
  scopeOptions: async (): Promise<{ stations: string[]; inverters: string[] }> => {
    const { data } = await api.get('/fleet/scope-options');
    return data;
  },
  // Inverter x time heatmap + worst-inverter ranking for a health pillar (fan/thermal/dc/eol).
  signalHeatmap: async (signal: string, limit = 48): Promise<SignalHeatmap> => {
    const { data } = await api.get('/fleet/signal-heatmap', { params: { signal, limit } });
    return data;
  },
  // Inverter x time heatmap of weekly DC-asymmetry (flagged units).
  dcBusResidual: async (): Promise<Record<string, DcBusResidual>> => {
    const { data } = await api.get('/fleet/dc-bus-residual');
    return data.residuals ?? {};
  },
  inverterDcStrings: async (serial: string): Promise<DcStrings> => {
    const { data } = await api.get(`/fleet/inverter/${encodeURIComponent(serial)}/dc-strings`);
    return data;
  },
  dcHeatmap: async (limit = 48): Promise<DcHeatmap> => {
    const { data } = await api.get('/fleet/dc-heatmap', { params: { limit } });
    return data;
  },
  // Daily Peck-component time-series (temp/RH/dew-point/dew-margin/wet-run/AF).
  peckSeries: async (): Promise<PeckSeries> => {
    const { data } = await api.get('/fleet/peck-series');
    return data;
  },
  // Tier-2 validation against the 5 confirmed UF112 failures (precursor + lead-time).
  tier2Validation: async (): Promise<Tier2Validation> => {
    const { data } = await api.get('/fleet/tier2-validation');
    return data;
  },
  // Deliverable-4 model-quality overview for the long-horizon RUL (Tier-1) view.
  rulOverview: async (): Promise<RulOverview> => {
    const { data } = await api.get('/fleet/rul-overview');
    return data;
  },
};

export interface RulOverview {
  fleet: {
    rul_median_months: number | null;
    ci95_halfband_median_months: number | null;
    ci95_halfband_p90_months: number | null;
    nominal_life_months: number;
    design_life_months?: number;
    rul_display_scale?: number;
    display_normalized?: boolean;
    n_inverters: number | null;
    ci95_note: string | null;
  };
  drivers: Array<{ feature: string; ig_weight: number }>;
  backtest: {
    run_id: string | null;
    kind: string | null;
    n_rows_scored: number | null;
    top_k: number | null;
    precision_at_topk: number | null;
    recall_at_topk: number | null;
    n_hits: number | null;
    n_failed_present: number | null;
    n_down_total: number | null;
    anchor: string | null;
    anchor_rank: number | null;
    hits_in_topk: string[];
    false_alarms_in_topk: string[];
    ranking_full: Array<{
      rank: number; inverter_id: string; rul_pred_median: number;
      ci95_half_median: number | null; is_down: boolean; is_anchor: boolean;
    }>;
    uf112_note: string | null;
    circularity_note: string | null;
    generated_at_utc: string | null;
  };
  deliverable4_pending: Array<{ metric: string; label: string; reason: string }>;
  val_r2: number | null;
  training_date: string | null;
  read_only: boolean;
  data_snapshot: string;
}

export interface Tier2ValidationCase {
  inverter_id: string;
  sr: string;
  uf112: string;
  quarter: string;
  precursor: string;
  lead_days: number;
  precursor_detected: boolean;
  in_window: boolean;
  live_proxy?: { signal: string; first_breach: string; lead_days: number; n_breach: number } | null;
}
export interface Tier2Validation {
  cases: Tier2ValidationCase[];
  summary: {
    n_failures: number; n_with_precursor: number; recall: number;
    lead_min_days: number; lead_max_days: number; lead_median_days: number;
    n_in_window: number; n_live_proxy: number; live_proxy_fleet: boolean;
    live_lead_median_days: number | null;
  };
}

export interface DcBusResidual { n: number; first: string | null; z: number; active: boolean; }
export interface DcStringInfo { name: string; active: boolean; share: number; series: (number | null)[]; }
export interface DcStrings { inverter_id: string; available: boolean; weeks: string[]; strings: DcStringInfo[]; note?: string; }
export interface DcHeatmap {
  weeks: string[];
  rows: { inverter_id: string; recent: number; dc: (number | null)[]; vdc?: DcBusResidual | null }[];
}

export interface SignalHeatmap {
  signal: string;
  weeks: string[];
  rows: { inverter_id: string; recent: number; vals: (number | null)[] }[];
}

export interface PeckSeries {
  dates: string[];
  air_temperature_c: (number | null)[];
  relative_humidity_pct: (number | null)[];
  dew_point_c: (number | null)[];
  dew_margin_c: (number | null)[];
  wet_run_h: (number | null)[];
  peck_af: (number | null)[];
}

export interface PredictionTimeline {
  index: string[];
  resolution: string;
  scope: string;
  scope_id: string | null;
  mode: string;
  n_inverters: number;
  series: Record<string, (number | null)[]>;
}

export interface EarlyWarningUnit {
  inverter_id: string;
  horizon: 'short' | 'short-medium' | 'medium';
  dc_early: number;
  dc_recent: number;
  signals: string[];
  dc_recent7?: number;
  dc_recent24?: number;
  age_days?: number | null;
  insulation?: { signals: string[]; n_breach: number; vpe_first?: string; pre_event?: boolean } | null;
}
export interface EarlyWarningResponse {
  as_of: string;
  peck: { rh_pct?: number; dew_point_c?: number; dew_margin_c?: number; peck_af?: number; peri?: number; flag?: string };
  n_flagged: number;
  units: EarlyWarningUnit[];
}

// Fase D — live forecast-vs-actual at the sim clock (Forecast Validation tab).
export interface LiveHorizonItem {
  horizon_days: number;
  oracle_window_end?: string;
  n_flagged_topk: number;
  n_positives_future?: number;
  precision_at_topk?: number;
  recall_at_topk?: number;
  n_total?: number;
}
export interface LiveHorizonReport {
  status: 'live' | 'pending';
  reference_timestamp?: string;
  model_source?: string;
  method: string;
  top_k: number;
  breach_threshold: number;
  horizons: LiveHorizonItem[];
  anchor?: { inverter_id: string; forecast_residual: number; rank?: number; n_inverters: number; flagged_topk: boolean };
  notes?: string;
  blocking: string[];
}
export const backtestApi = {
  liveHorizon: async (params?: { horizons_days?: string; top_k?: number }): Promise<LiveHorizonReport> => {
    const { data } = await api.get('/backtest/live-horizon', { params });
    return data;
  },
  q4Report: async (): Promise<any> => {
    const { data } = await api.get('/backtest/q4/report');
    return data;
  },
};

export default api;
